import React, { useState, useEffect } from 'react';
import { X, Plus, Gift, Search, Palette, Trash2 } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { GiftCardSettings } from '../services/giftCardSettingsService';
import GiftCardWrapper from './GiftCardWrapper';

interface GiftCardsManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateNewCard: () => void;
  onEditCard: (design: GiftCardSettings) => void;
  refreshTrigger?: number;
}

export default function GiftCardsManagementModal({ isOpen, onClose, onCreateNewCard, onEditCard, refreshTrigger }: GiftCardsManagementModalProps) {
  const { selectedBusiness } = useBusiness();
  const [cardDesigns, setCardDesigns] = useState<GiftCardSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadCardDesigns();
    }
  }, [isOpen, selectedBusiness?.id, refreshTrigger]);

  const loadCardDesigns = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('gift_card_settings')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCardDesigns(data || []);
    } catch (error) {
      console.error('Error loading card designs:', error);
      toast.error('Failed to load card designs');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleDeleteDesign = async (designId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    
    if (!confirm('Are you sure you want to delete this gift card design?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('gift_card_settings')
        .delete()
        .eq('id', designId);

      if (error) throw error;

      toast.success('Gift card design deleted');
      loadCardDesigns(); // Refresh the list
    } catch (error) {
      console.error('Error deleting gift card design:', error);
      toast.error('Failed to delete gift card design');
    }
  };

  const filteredDesigns = cardDesigns.filter(design => 
    design.card_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    design.greeting_message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        } z-[9999]`}
        onClick={handleClose}
      >
        <div 
          className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          onTransitionEnd={handleTransitionEnd}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="bg-gray-900 border-b border-gray-800">
              <div className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  <div className="flex items-center space-x-2">
                    <div className="p-2 bg-blue-600 rounded-lg">
                      <Gift className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-white">My Cards</h2>
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleClose();
                    onCreateNewCard();
                  }}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Create New Card</span>
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="p-6 space-y-6">
                
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search card designs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Stats */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Card Designs</p>
                      <p className="text-2xl font-bold text-gray-900">{cardDesigns.length}</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <Palette className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                {/* Card Designs List */}
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredDesigns.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-12">
                    <div className="text-center">
                      <Palette className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg font-medium">No card designs yet</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {searchTerm ? 'Try adjusting your search' : 'Click "Create New Card" to design your first gift card'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredDesigns.map((design) => (
                      <div
                        key={design.id}
                        onClick={() => {
                          handleClose();
                          onEditCard(design);
                        }}
                        className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Palette className="h-5 w-5 text-purple-600" />
                              <h3 className="font-semibold text-gray-900">{design.card_title}</h3>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className="text-xs text-gray-500">
                                {new Date(design.created_at!).toLocaleDateString()}
                              </span>
                              <button
                                onClick={(e) => handleDeleteDesign(design.id!, e)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete design"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="w-full max-w-md pointer-events-none">
                            <GiftCardWrapper
                              settings={design}
                              businessLogoUrl={selectedBusiness?.booking_settings?.logo_url}
                              showSampleData={true}
                              maxWidth="100%"
                            />
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                            <span className="px-2 py-1 bg-gray-100 rounded">
                              Style: {design.card_style}
                            </span>
                            <span className="px-2 py-1 bg-gray-100 rounded">
                              Font: {design.font_family}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

